/* originKind.js — one answer to "what kind of origin is this bundle running on".
 *
 * WHY THIS EXISTS. `api/client.js` and `SkillTreeViewport/stowedNodes.js` each
 * decided this for themselves, and both decided it the same wrong way: they
 * named the origins they recognised as PRODUCTION and let everything else fall
 * through to dev behaviour. That is fail-open, and it has two live consequences.
 *
 *   1. A packaged native shell (Capacitor/Cordova) serves the bundle from
 *      `capacitor://localhost`, whose port is the empty string. Every
 *      port-based branch in client.js misses, so the base URL falls through to
 *      `http://localhost:7000` — a cleartext request to the DEVICE itself,
 *      which App Transport Security blocks outright.
 *   2. `stowedNodes.js` hid the psilocybin node only on `debono.ai`. A tailnet
 *      address reaching the prod container (see `utils/envFlags.js`) is not
 *      `debono.ai`, so the node the owner ordered stowed rendered anyway.
 *
 * So this module inverts the default: an origin is a DEV surface only when it
 * is positively recognised as one, and everything else is production. Adding a
 * new production hostname can no longer silently open a dev-only surface.
 *
 * Callers pass a location-like object so this is testable without a real host.
 */

/** Frontend ports the dev stack binds, mirroring the ladder in `api/client.js`. */
const DEV_FRONTEND_PORTS = new Set([
  '3000', '3010', '3020', '3030', '3040', '3050', '3060', '3070',
  '7013', '7015', '7018',
]);

/** A bare IPv4 literal, as opposed to a DNS name. */
const BARE_IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;

/**
 * RFC1918 private space plus loopback. A private address is someone's own
 * machine or LAN, so it can only be a dev surface.
 *
 * Deliberately NOT 100.64/10: tailnet addresses are carrier-grade NAT space and
 * they reach the PRODUCTION container, so they must never read as private.
 */
const PRIVATE_IPV4 = /^(?:10\.|192\.168\.|172\.(?:1[6-9]|2\d|3[01])\.|127\.)/;

function currentLocation() {
  if (typeof window === 'undefined' || !window.location) return null;
  return window.location;
}

/**
 * True when the bundle is running inside a packaged native shell rather than a
 * browser tab. Checked before any hostname test, because a native shell's
 * hostname is a lie: Capacitor on iOS reports `localhost`, which would
 * otherwise read as a developer machine.
 *
 * There is no native shell in the tree today, so this returns false in every
 * current environment and the callers below are unchanged until one is added.
 */
export function isNativeShell(loc = currentLocation()) {
  if (typeof window !== 'undefined' && window) {
    // Capacitor's own API is the authority when the runtime is present.
    const cap = window.Capacitor;
    if (cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) return true;
    if (window.cordova) return true;
  }
  if (!loc) return false;
  const protocol = String(loc.protocol || '').toLowerCase();
  // capacitor:// and ionic:// are the packaged schemes; file:// is Cordova's.
  return protocol === 'capacitor:' || protocol === 'ionic:' || protocol === 'file:';
}

/**
 * True only for an origin positively recognised as a developer or preprod
 * surface. Everything else, including anything unrecognised, is production.
 *
 * A bare IP is ambiguous on its own, because the same address reaches the dev
 * container or the prod container depending on the port. So an IP is a dev
 * surface only on a known dev frontend port, which is the same rule
 * `api/client.js` already applies when it picks a backend port.
 */
export function isDevSurface(loc = currentLocation()) {
  if (!loc) return false;
  // A packaged binary is never a dev surface, whatever hostname it reports.
  if (isNativeShell(loc)) return false;

  const host = String(loc.hostname || '').toLowerCase();
  const port = String(loc.port || '');

  if (host.includes('debono-preprod')) return true;
  if (host === 'localhost' || host === '127.0.0.1') return true;
  if (PRIVATE_IPV4.test(host)) return true;
  if (BARE_IPV4.test(host)) return DEV_FRONTEND_PORTS.has(port);
  return false;
}

/**
 * The inverse of `isDevSurface`, named for the question callers actually ask.
 * Fail-closed: an origin nobody anticipated is treated as production.
 */
export function isProductionSurface(loc = currentLocation()) {
  return !isDevSurface(loc);
}
